import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { getAuth, signOut } from 'firebase/auth'

import {
  getCustomerInfoAction,
  getCustomerInfoByEmailAction,
  getPaymentDataAction,
  getUserSubscriptionsInfoUnifiedAction,
  setAuthTokenAction,
  setEmailAuthAction,
} from 'root-redux/actions/user'
import { selectAppName } from 'root-redux/selects/common'
import {
  selectAuthToken,
  selectEmailAuth,
  selectUUID,
  selectUserSubscriptionInfo,
} from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { EMAIL_AUTH_APPS } from 'root-constants'

import { useRetentionFlowApp } from './useRetentionFlowApp'

export const useUserData = () => {
  const { search } = useLocation()
  const dispatch: TAppDispatch = useDispatch()
  const authToken = useSelector(selectAuthToken)
  const emailAuth = useSelector(selectEmailAuth)
  const userSubscriptionInfo = useSelector(selectUserSubscriptionInfo)
  const userId = useSelector(selectUUID)
  const appName = useSelector(selectAppName)

  const isRetentionFlowSupportApp = useRetentionFlowApp()
  const isEmailAuthApp = EMAIL_AUTH_APPS.includes(appName)

  const signOutFirebase = useCallback(() => signOut(getAuth()), [])

  useEffect(() => {
    if (userSubscriptionInfo) return

    if (authToken || emailAuth) {
      dispatch(getUserSubscriptionsInfoUnifiedAction(signOutFirebase))
    }
  }, [authToken, emailAuth, dispatch, signOutFirebase, userSubscriptionInfo])

  useEffect(() => {
    if (authToken || emailAuth) return

    const query = new URLSearchParams(
      search.replace(/\+/g, encodeURIComponent('+')),
    )

    if (isEmailAuthApp) {
      const rawEmail = query.get('ref')
      let email: string | null = null

      if (rawEmail) {
        try {
          email = window.atob(rawEmail)
        } catch {
          email = rawEmail
          query.set('ref', window.btoa(rawEmail))
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}?${query.toString()}`,
          )
        }
      }

      if (email) {
        dispatch(setEmailAuthAction(email))
        dispatch(getCustomerInfoByEmailAction({ hasEvent: false }))
        return
      }

      goTo(PageId.LOGIN)
      return
    }

    const token = query.get('token')

    if (token) {
      dispatch(setAuthTokenAction(token))
      dispatch(getCustomerInfoAction({ hasEvent: false }))
      return
    }

    goTo(PageId.LOGIN)
  }, [authToken, emailAuth, dispatch, isEmailAuthApp, search])

  useEffect(() => {
    if (userId && isRetentionFlowSupportApp) {
      dispatch(getPaymentDataAction())
    }
  }, [userId, isRetentionFlowSupportApp, dispatch])
}
